import dayjs from "dayjs";

import { RedDay } from "us.collection.admin/constants";
import { CopyTypeKey } from "us.collection.admin/interfaces";
import { DateFormats } from "us.common/constants";
import { COPY_TYPE_LABELS } from "../constants";

/**
 * @description - Given a start and end date, returns an array of dayjs objects representing each day in the range (inclusive).
 * If the start or end date is invalid, returns an empty array.
 * The function normalizes the start and end dates to the start of their respective days to ensure consistency.
 * @param start - The starting dayjs object of the range.
 * @param end - The ending dayjs object of the range.
 * @returns An array of dayjs objects for each day in the range, or an empty array if input is invalid.
 */
const expandRange = (start: dayjs.Dayjs, end: dayjs.Dayjs): dayjs.Dayjs[] => {
  try {
    const days: dayjs.Dayjs[] = [];
    let cursor = start.startOf("day");
    const last = end.startOf("day");
    while (cursor.isSameOrBefore(last)) {
      days.push(cursor);
      cursor = cursor.add(1, "day");
    }
    return days;
  } catch (error) {
    return [];
  }
};

/**
 * The API `RedDay` model stores channels as booleans (`isNonSMSDay`, etc)
 * while CopyCalendar's UI uses `CopyTypeKey` keys (`nonSms`, etc).
 */
export const copyTypeToRedDayField: Record<CopyTypeKey, keyof RedDay> = {
  nonPrinting: "isNonPrintDay",
  nonSms: "isNonSMSDay",
  nonEmail: "isNonEmailDay",
  nonEhf: "isNonEhfDay",
};

export const getCopyItemChannels = (
  types: Record<CopyTypeKey, boolean> | undefined,
): CopyTypeKey[] => {
  try {
    return (Object.keys(COPY_TYPE_LABELS) as CopyTypeKey[]).filter(
      (k) => !!types?.[k],
    );
  } catch (error) {
    return [];
  }
};

export const getRedDayChannels = (redDay: RedDay): CopyTypeKey[] => {
  try {
    return (Object.keys(COPY_TYPE_LABELS) as CopyTypeKey[]).filter(
      (k) => !!(redDay as any)?.[copyTypeToRedDayField[k]],
    );
  } catch (error) {
    return [];
  }
};

/**
 * Filter already-saved redDays down to the ones that touch the target year.
 * We keep items that start OR end in the year (ranges can cross year boundaries).
 */
export const getRedDaysForYear = (
  redDays: RedDay[] | undefined,
  targetYear: number,
): RedDay[] => {
  try {
    if (!Array.isArray(redDays) || redDays.length === 0) return [];

    return redDays.filter((d) => {
      const s = dayjs(d.startDate);
      const e = dayjs(d.endDate);
      const startYear = s.isValid()
        ? s.year()
        : new Date(d.startDate).getFullYear();
      const endYear = e.isValid()
        ? e.year()
        : new Date(d.endDate).getFullYear();
      return startYear === targetYear || endYear === targetYear;
    });
  } catch (error) {
    return [];
  }
};

export const overlapsInclusiveByDay = (
  aStart: dayjs.Dayjs,
  aEnd: dayjs.Dayjs,
  bStart: dayjs.Dayjs,
  bEnd: dayjs.Dayjs,
): boolean => {
  try {
    if (
      !aStart.isValid() ||
      !aEnd.isValid() ||
      !bStart.isValid() ||
      !bEnd.isValid()
    ) {
      return false;
    }
    return !aEnd.isBefore(bStart, "day") && !bEnd.isBefore(aStart, "day");
  } catch (error) {
    return false;
  }
};

export const isExactSameRangeByDay = (
  aStart: dayjs.Dayjs,
  aEnd: dayjs.Dayjs,
  bStart: dayjs.Dayjs,
  bEnd: dayjs.Dayjs,
): boolean => {
  try {
    if (
      !aStart.isValid() ||
      !aEnd.isValid() ||
      !bStart.isValid() ||
      !bEnd.isValid()
    ) {
      return false;
    }
    return aStart.isSame(bStart, "day") && aEnd.isSame(bEnd, "day");
  } catch (error) {
    return false;
  }
};

export type CopyItemConflict = {
  redDay: RedDay;
  overlappingChannels: CopyTypeKey[];
  exactSameDates: boolean;
};

/**
 * Finds conflicts between a CopyCalendar item and already-saved redDays.
 *
 * Conflict rules (per product requirements):
 * - Overlapping date range + overlapping channel(s) => conflict
 * - Exact same date range (start+end match) => conflict even if channels differ
 */
export const findCopyItemConflicts = (params: {
  item: {
    id?: number;
    startDate: string;
    endDate: string;
    types: Record<CopyTypeKey, boolean>;
  };
  existingRedDays: RedDay[];
}): CopyItemConflict[] => {
  try {
    const { item, existingRedDays } = params;

    const aStart = dayjs(item.startDate).startOf("day");
    const aEnd = dayjs(item.endDate).startOf("day");
    if (!aStart.isValid() || !aEnd.isValid()) return [];

    const conflicts: CopyItemConflict[] = [];
    for (const rd of existingRedDays) {
      // If we are editing an existing item, don't mark it as conflicting with itself.
      if (typeof item.id === "number" && rd.id === item.id) continue;

      const bStart = dayjs(rd.startDate).startOf("day");
      const bEnd = dayjs(rd.endDate).startOf("day");
      if (!overlapsInclusiveByDay(aStart, aEnd, bStart, bEnd)) continue;

      const exactSameDates = isExactSameRangeByDay(aStart, aEnd, bStart, bEnd);

      const overlappingChannels = (
        Object.keys(COPY_TYPE_LABELS) as CopyTypeKey[]
      ).filter(
        (k) => !!item.types?.[k] && !!(rd as any)?.[copyTypeToRedDayField[k]],
      );

      if (overlappingChannels.length || exactSameDates) {
        conflicts.push({ redDay: rd, overlappingChannels, exactSameDates });
      }
    }

    return conflicts;
  } catch (error) {
    return [];
  }
};

/**
 * @description - Evaluates the status of a copy item against existing coverage and target year constraints.
 * @param item - An object containing startDate, endDate, and types for the copy item being evaluated.
 * @param coverage - A map of existing coverage for each day in the target year.
 * @param year - The target year for which the copy item is being evaluated.
 * @returns An object containing the status of the copy item and optional notes explaining conflicts.
 */
export const evaluateStatus = (
  item: {
    startDate: string;
    endDate: string;
    types: Record<CopyTypeKey, boolean>;
  },
  coverage: Map<string, Record<CopyTypeKey, boolean>>,
  year: number,
): { status: number; notes?: string } => {
  try {
    const start = dayjs(item.startDate).startOf("day");
    const end = dayjs(item.endDate).startOf("day");
    if (!start.isValid() || !end.isValid())
      return { status: 2, notes: "Invalid date" };
    if (start.year() !== year || end.year() !== year)
      return { status: 2, notes: "Out of target year" };
    if (end.isBefore(start)) return { status: 2, notes: "Invalid date range" };

    const dates = expandRange(start, end);
    const selectedTypes = (Object.keys(item.types) as CopyTypeKey[]).filter(
      (k) => item.types[k],
    );
    if (selectedTypes.length === 0)
      return { status: 2, notes: "No types selected" };

    let coveredCount = 0;
    let anyOverlap = false;

    for (const d of dates) {
      const existing = coverage.get(dayjs(d).format(DateFormats.FORMAT_1));
      if (!existing) continue;

      const fullyCoveredForDay = selectedTypes.every(
        (typeKey) => existing[typeKey] === true,
      );
      if (fullyCoveredForDay) {
        coveredCount++;
        anyOverlap = true;
      } else {
        anyOverlap = true;
      }
    }

    if (!anyOverlap) return { status: 0 };
    if (coveredCount === dates.length) return { status: 1 };
    return { status: 2, notes: "Overlaps existing calendar" };
  } catch (error) {
    return { status: 2, notes: "Error evaluating item" };
  }
};
